import { createSlice } from "@reduxjs/toolkit"

import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { RESULT_TYPES } from "../constants"
import {
  changeEmail,
  confirmEmail,
  fetchIdConfiguration,
  fetchUser,
  requestAccountDeletion,
  updateIdConfiguration,
  updatePassword,
  updateUser
} from "./settings.thunks"
import { type SettingsState } from "./settings.types.ts"

const initialState: SettingsState = {
  fetching: false,
  saveFetching: false,
  savePasswordFetching: false,
  deleteAccountFetching: false,

  user: null,
  // Only the newest `fetchUser` may write. Switching the workspace
  // selector (Company HQ <-> a specific branch) twice quickly — or
  // reopening this page while an earlier fetch for a different branch is
  // still in flight — otherwise lets the slower, older request land last
  // and silently repaint the branch the user already switched away from.
  fetchUserRequestId: null,
  // The branch (or `undefined` for Company HQ) `user.company_details`
  // currently reflects — lets `updateUser.fulfilled` refuse to merge a
  // save response meant for a branch the workspace selector has since
  // moved away from (started a save on LA, switched to BO before it
  // resolved, BO's own GET already landed) into what is now BO's data.
  loadedTeam: undefined,
  saveResult: null,

  idConfiguration: null,
  idConfigurationFetching: false,
  idConfigurationSaving: false
}

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: { setSettingsProp: generateSetPropReducer<SettingsState>() },
  extraReducers: (builder) => {
    builder
      // fetchUser
      .addCase(fetchUser.pending, (state, action) => {
        state.fetching = true
        state.fetchUserRequestId = action.meta.requestId
        state.user = null
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        if (state.fetchUserRequestId !== action.meta.requestId) return
        state.fetching = false
        state.fetchUserRequestId = null
        state.user = action.payload
        const arg = action.meta.arg
        state.loadedTeam = typeof arg === "string" ? undefined : arg.team
      })
      .addCase(fetchUser.rejected, (state, action) => {
        if (state.fetchUserRequestId !== action.meta.requestId) return
        state.fetching = false
        state.fetchUserRequestId = null
      })

      // updateUser
      .addCase(updateUser.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const user = action?.payload

        state.saveFetching = false
        // The workspace selector may have moved to a different branch
        // while this save was in flight (its own GET already landed and
        // repainted `state.user`/`state.loadedTeam`) — merging a save
        // response meant for the branch the user has since left would
        // silently overwrite the branch now on screen with stale data.
        if (action.meta.arg?.team !== state.loadedTeam) return
        state.user = {
          ...(state.user ? { ...state.user } : {}),
          ...user
        }
        // state.saveResult = { type: RESULT_TYPES.USER_UPDATE }
      })
      .addCase(updateUser.rejected, (state) => {
        state.saveFetching = false
      })

      // updatePassword
      .addCase(updatePassword.pending, (state) => {
        state.savePasswordFetching = true
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.savePasswordFetching = false
        // state.saveResult = { type: RESULT_TYPES.PASSWORD_UPDATE }
      })
      .addCase(updatePassword.rejected, (state) => {
        state.savePasswordFetching = false
      })

      // changeEmail
      .addCase(changeEmail.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(changeEmail.fulfilled, (state, action) => {
        state.saveFetching = false
        // state.saveResult = { type: RESULT_TYPES.EMAIL_UPDATE_REQUEST }
      })
      .addCase(changeEmail.rejected, (state) => {
        state.saveFetching = false
      })

      // confirmEmail
      .addCase(confirmEmail.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(confirmEmail.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = { type: RESULT_TYPES.EMAIL_UPDATE_CONFIRM }
      })
      .addCase(confirmEmail.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(requestAccountDeletion.pending, (state) => {
        state.deleteAccountFetching = true
      })
      .addCase(requestAccountDeletion.fulfilled, (state, action) => {
        state.deleteAccountFetching = false
        const user = action.payload
        if (user && state.user) {
          state.user = { ...state.user, ...user }
        } else if (user) {
          state.user = user as typeof state.user
        }
      })
      .addCase(requestAccountDeletion.rejected, (state) => {
        state.deleteAccountFetching = false
      })

      // fetchIdConfiguration
      .addCase(fetchIdConfiguration.pending, (state) => {
        state.idConfigurationFetching = true
      })
      .addCase(fetchIdConfiguration.fulfilled, (state, action) => {
        state.idConfigurationFetching = false
        state.idConfiguration = action.payload
      })
      .addCase(fetchIdConfiguration.rejected, (state) => {
        state.idConfigurationFetching = false
      })

      // updateIdConfiguration
      .addCase(updateIdConfiguration.pending, (state) => {
        state.idConfigurationSaving = true
      })
      .addCase(updateIdConfiguration.fulfilled, (state, action) => {
        state.idConfigurationSaving = false
        state.idConfiguration = action.payload
      })
      .addCase(updateIdConfiguration.rejected, (state) => {
        state.idConfigurationSaving = false
      })

      .addCase(logout.fulfilled, () => {
        console.log("settings logout")
        return initialState
      })
  }
})

export const { setSettingsProp } = settingsSlice.actions

export default settingsSlice
